import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { formatNumber, inputClass } from '../ui/FormControls';
import { cn } from '../../lib/utils';

interface AvailableSerial {
  id: string;
  serialNumber: string;
  batchNumber: string | null;
  workOrderNumber: string | null;
}

interface SerialPickerProps {
  productId: string;
  productName: string;
  // How many units are leaving; that many numbers have to be chosen
  wanted: number;
  value: string[];
  onChange: (serialNumbers: string[]) => void;
}

// Choose which units of a product kept by serial number go out: one number for each piece
export const SerialPicker: React.FC<SerialPickerProps> = ({ productId, productName, wanted, value, onChange }) => {
  const { data: available = [], isLoading } = useQuery({
    queryKey: ['serials', 'available', productId],
    queryFn: async (): Promise<AvailableSerial[]> => (await api.get(`/serials/available/${productId}`)).data,
  });
  const [filter, setFilter] = useState('');
  const needle = filter.trim().toLowerCase();
  const shown = needle ? available.filter((serial) => serial.serialNumber.toLowerCase().includes(needle) || (serial.batchNumber ?? '').toLowerCase().includes(needle)) : available;
  const whole = Number.isInteger(wanted) && wanted > 0 ? wanted : 0;
  const toggle = (serialNumber: string) => onChange(value.includes(serialNumber) ? value.filter((chosen) => chosen !== serialNumber) : [...value, serialNumber]);

  return (
    <div className="rounded-md bg-gray-50 p-3 ring-1 ring-gray-200">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Serial numbers</p>
        <p className={cn('text-xs', value.length === whole && whole > 0 ? 'text-green-700' : 'text-amber-700')}>
          {formatNumber(value.length)} of {formatNumber(whole)} chosen · {formatNumber(available.length)} in stock
        </p>
        {available.length > 8 && (
          <input type="search" value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Find a number or batch" aria-label={`Find a serial number of ${productName}`} className={cn(inputClass, 'w-52 py-1')} />
        )}
        {whole > 0 && available.length >= whole && (
          <button type="button" onClick={() => onChange(available.slice(0, whole).map((serial) => serial.serialNumber))} className="text-xs font-semibold text-indigo-600 hover:text-indigo-500" aria-label={`Take the oldest ${whole} of ${productName}`}>
            Take the oldest {formatNumber(whole)}
          </button>
        )}
        {value.length > 0 && (
          <button type="button" onClick={() => onChange([])} className="text-xs font-semibold text-gray-500 hover:text-gray-700">
            Clear
          </button>
        )}
      </div>
      {isLoading ? (
        <p className="mt-2 text-sm text-gray-500">Loading…</p>
      ) : available.length === 0 ? (
        <p className="mt-2 text-sm text-red-600">No unit of {productName} is in stock by serial number. Register the units, or complete the work order that makes them.</p>
      ) : (
        <div className="mt-2 flex max-h-36 flex-wrap gap-1.5 overflow-y-auto">
          {shown.map((serial) => {
            const chosen = value.includes(serial.serialNumber);
            return (
              <label key={serial.id} title={[serial.batchNumber && `Batch ${serial.batchNumber}`, serial.workOrderNumber].filter(Boolean).join(' · ') || undefined} className={cn('flex cursor-pointer items-center gap-1.5 rounded-md px-2 py-1 text-xs ring-1 ring-inset', chosen ? 'bg-indigo-600 text-white ring-indigo-600' : 'bg-white text-gray-700 ring-gray-300 hover:bg-gray-100')}>
                <input type="checkbox" checked={chosen} onChange={() => toggle(serial.serialNumber)} aria-label={`${productName} ${serial.serialNumber}`} className="sr-only" />
                {serial.serialNumber}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SerialPicker;
